import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import Cookies from 'js-cookie';
import { getAccountId, getData } from '../utils/utils';
import { DataContext } from './DataContext';

interface Location {
  active_activity_set: string,
  activity_sets: string[],
  address: {
    description?: string;
    [key: string]: any;
  },
  admin_members: string[],
  child_ids: string[],
  created_at: string,
  created_by: string,
  current_invite_key: string,
  floor_space: number,
  id: string,
  identifier: string,
  identifier_type: string,
  invite_key_exp: string,
  is_deleted: boolean,
  merged_from: string[],
  name: string,
  number_of_staff: number,
  parent_id: string,
  region: string,
  turnover: number,
  updated_at: string,
  tags: string[],
  is_starred: boolean,
  member_list: [
    {
      created_at: '',
      email: '',
      first_name: '',
      id: '',
      last_name: '',
      license: '',
      role: '',
    },
  ],
  currency?: string,
}

interface LocationContextType {
  locations: Location[];
  setLocations: Dispatch<SetStateAction<Location[]>>;
  refreshLocations: () => Promise<void>;
  selectedOrg: string;
  setSelectedOrg: Dispatch<SetStateAction<string>>;
}

export const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

// ✅ This resolves the "function component is not a function declaration" warning
export function LocationProvider({ children }: LocationProviderProps) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>(
    Cookies.get('selectedOrganisationName') || '',
  );
  const { setMessagebar } = useContext(DataContext);

  const refreshLocations = async () => {
    try {
      const accountId = getAccountId();
      if (accountId && accountId.accountId) {
        const userRes = await getData(`/accounts/${accountId.accountId}`);
        const orgId = userRes.data.organisation;

        const locationRes = await getData(`/entities/?organisation_id=${orgId}&is_archive=false`);
        if (locationRes.status === 200) {
          setLocations(locationRes.data || []);
        }
      }
    } catch (err) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Failed to fetch account details.',
        severity: 'error',
      }));
    }
  };

  useEffect(() => {
    refreshLocations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      Cookies.set('selectedOrganisationName', selectedOrg);
    }
  }, [selectedOrg]);

  const value = useMemo(
    () => ({
      locations,
      setLocations,
      refreshLocations,
      selectedOrg,
      setSelectedOrg,
    }),
    [locations, selectedOrg],
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export const useLocationContext = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
};
